import { useSearchParams } from 'react-router-dom'
import CtaBand from '../components/CtaBand'
import PageHero from '../components/PageHero'
import PostCard, { PostRow } from '../components/PostCard'
import Reveal from '../components/Reveal'
import { Section } from '../components/Section'
import { categories, posts } from '../articles'
import {
  ORG_ID,
  WEBSITE_ID,
  absoluteUrl,
  plain,
  useJsonLd,
  useSeo,
} from '../lib/seo'

/*
 * The index as a Blog with its posts listed.
 *
 * Built from the unfiltered list on purpose: the category filter lives in the
 * query string, and describing a filtered subset would make the structured data
 * disagree with the canonical URL, which always points at `/insights`.
 */
const blogJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${absoluteUrl('/insights')}#blog`,
  url: absoluteUrl('/insights'),
  name: 'Insights',
  description:
    'Expertentipps für effizientes Immobilienmanagement und Neuerungen im Bereich Hausverwaltung.',
  inLanguage: 'de-DE',
  isPartOf: { '@id': WEBSITE_ID },
  publisher: { '@id': ORG_ID },
  blogPost: posts.map((post) => ({
    '@type': 'BlogPosting',
    '@id': `${absoluteUrl(`/insights/${post.slug}`)}#article`,
    headline: plain(post.title),
    description: plain(post.excerpt),
    datePublished: post.date,
    articleSection: post.category,
    url: absoluteUrl(`/insights/${post.slug}`),
    author: { '@id': ORG_ID },
  })),
}

/**
 * Insights index.
 *
 * The category lives in the query string so a filtered view can be linked and
 * survives a reload. Because only the search params change, the scroll position
 * stays put when you switch category (see components/layout/Layout.jsx).
 */
export default function Insights() {
  const [params, setParams] = useSearchParams()
  const active = params.get('kategorie') ?? ''

  useSeo({
    title: 'Insights',
    description:
      'Expertentipps für effizientes Immobilienmanagement und Neuerungen im Bereich Hausverwaltung - Ihr Informationszentrum für alles rund um Ihre Immobilie.',
    path: '/insights',
  })
  useJsonLd(blogJsonLd, 'blog')

  const filtered = active
    ? posts.filter((post) => post.category === active)
    : posts

  const showEditorial = !active && posts.length >= 3
  const [lead, ...rest] = filtered
  const sidebar = showEditorial ? rest.slice(0, 2) : []
  const grid = showEditorial ? rest.slice(2) : filtered

  const setCategory = (category) => {
    if (category) setParams({ kategorie: category })
    else setParams({})
  }

  return (
    <>
      <PageHero
        eyebrow="Hausverwalter Insights"
        title="Aktuelle Einblicke, Ratgeber und Expertenwissen"
        lead="Entdecken Sie Expertentipps für effizientes Immobilienmanagement und Neuerungen im Bereich Hausverwaltung - Ihr Informationszentrum für alles rund um Ihre Immobilie."
        breadcrumb={[{ label: 'Insights' }]}
      />

      <Section>
        {/* Category filter */}
        <div
          className="btn-row"
          style={{ marginBottom: 'var(--s-7)', alignItems: 'center' }}
        >
          <button
            type="button"
            className={`btn btn--sm btn--${active ? 'outline' : 'secondary'}`}
            onClick={() => setCategory('')}
            aria-pressed={!active}
          >
            <span>Alle Beiträge</span>
          </button>
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`btn btn--sm btn--${
                active === category ? 'secondary' : 'outline'
              }`}
              onClick={() => setCategory(category)}
              aria-pressed={active === category}
            >
              <span>{category}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p>Für diese Kategorie liegen noch keine Beiträge vor.</p>
        ) : null}

        {showEditorial ? (
          <Reveal className="insights-lead" style={{ marginBottom: 'var(--s-8)' }}>
            <PostCard post={lead} />
            <div className="insights-lead__side">
              {sidebar.map((post) => (
                <PostRow post={post} key={post.slug} />
              ))}
            </div>
          </Reveal>
        ) : null}

        {grid.length > 0 ? (
          <>
            {showEditorial ? (
              <h2 style={{ marginBottom: 'var(--s-5)' }}>Alle Blogartikel</h2>
            ) : null}
            <Reveal group className="grid grid--3">
              {grid.map((post) => (
                <PostCard post={post} key={post.slug} />
              ))}
            </Reveal>
          </>
        ) : null}
      </Section>

      <CtaBand />
    </>
  )
}
